/**
 * W123.4 · 2026-05-26 · AjudaContextual — substitui Housy como módulo.
 *
 * Cravado em HANDOFF-2026-05-25-REFACTOR-UX-W122 §S4/P9:
 *   - Housy NÃO é chat. Vira um ícone "?" no canto superior direito.
 *   - Click abre drawer lateral 320px com 3 conteúdos pré-carregados:
 *       1. "O que é esta tela"  — descrição curta.
 *       2. "O que faço agora"   — próxima ação contextual.
 *       3. "Glossário"          — termos da tela atual.
 *   - Marca "Housy" NÃO aparece pro PM — chamado "Ajuda".
 *   - V1 sem chat livre, sem busca. Conteúdo estático contextual.
 *
 * Como cada página seta seu contexto:
 *   ```ts
 *   const setCtx = useSetAjudaContext();
 *   useEffect(() => {
 *     setCtx({ id: 'plano', title: 'Plano da obra', ... });
 *     return () => setCtx(null);
 *   }, []);
 *   ```
 * Quando o contexto é null, o botão "?" fica oculto (não temos nada
 * pra ensinar). Quando existe, o drawer abre com os 3 blocos.
 *
 * Acessibilidade: drawer é `role="dialog"` com `aria-labelledby`. ESC
 * fecha. Botão de fechar tem `aria-label`. Não usa `useT()` pra labels
 * dos 3 blocos porque eles vêm do `pageContext` (já localizado por
 * quem chama).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useT } from '@/hooks/useT';
import { cn } from '@/lib/cn';

/**
 * Contexto que cada página seta pra alimentar o drawer Ajuda. `null`
 * quando a página não declarou contexto — botão "?" fica oculto.
 */
export interface AjudaPageContext {
  /** Id estável da tela (telemetria + memoização). */
  id: string;
  /** Título do bloco "O que é esta tela" (4-8 palavras). */
  title: string;
  /** Descrição curta. 1-3 frases. */
  description: string;
  /**
   * Próxima ação sugerida. Opcional — quando ausente, o bloco
   * "O que faço agora" some.
   */
  nextAction?: {
    label: string;
    /** Rota interna OU descrição da ação inline ("Clique no botão Enviar"). */
    to?: string;
  };
  /** Termos da tela explicados em 1 linha cada. Vazio → bloco some. */
  glossary?: Array<{ term: string; meaning: string }>;
}

interface AjudaContextValue {
  ctx: AjudaPageContext | null;
  setCtx: (ctx: AjudaPageContext | null) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
}

const AjudaCtx = createContext<AjudaContextValue | null>(null);

/**
 * Provider montado pelo AppShell. Mantém o contexto da página corrente
 * e o estado open/closed do drawer. Páginas chamam `useSetAjudaContext`
 * pra publicar seu contexto.
 */
export function AjudaContextualProvider({ children }: { children: ReactNode }) {
  const [ctx, setCtx] = useState<AjudaPageContext | null>(null);
  const [open, setOpen] = useState(false);
  const value = useMemo<AjudaContextValue>(
    () => ({ ctx, setCtx, open, setOpen }),
    [ctx, open],
  );
  return <AjudaCtx.Provider value={value}>{children}</AjudaCtx.Provider>;
}

/**
 * Hook usado por cada página pra publicar seu contexto de ajuda.
 * Retorna uma função ESTÁVEL (mesma referência entre renders) pra que
 * páginas possam listar `setCtx` como dep de useEffect sem provocar
 * re-fire infinito: `v` (o valor do Context) muda quando ctx muda, e
 * se a callback fosse derivada de `v` ela mudaria também — gerando
 * loop. A ref + callback estável quebra esse ciclo.
 */
export function useSetAjudaContext(): (ctx: AjudaPageContext | null) => void {
  const v = useContext(AjudaCtx);
  const ref = useRef<AjudaContextValue | null>(v);
  ref.current = v;
  return useCallback((next: AjudaPageContext | null) => {
    if (ref.current) ref.current.setCtx(next);
  }, []);
}

/** Hook interno usado pelo botão e pelo drawer. */
function useAjudaState(): AjudaContextValue | null {
  return useContext(AjudaCtx);
}

/**
 * W124.2 · 2026-05-26 · Permite outros componentes (ex: ProjectHero)
 * abrirem o drawer de ajuda sem precisar renderizar o `?` flutuante.
 * Retorna `null` quando o provider não está montado OU quando a página
 * corrente não publicou contexto — caller pode esconder o seu botão
 * próprio nesse caso.
 */
export function useOpenAjuda(): (() => void) | null {
  const state = useAjudaState();
  if (!state || !state.ctx) return null;
  return () => state.setOpen(true);
}

/**
 * Botão circular "?" que abre o drawer. Renderiza só quando há um
 * contexto publicado pela página corrente. O AppShell monta este
 * botão e o drawer juntos via `<AjudaContextualMount />` abaixo.
 */
function AjudaButton() {
  const state = useAjudaState();
  const { t } = useT();
  if (!state || !state.ctx) return null;
  const label = t.ajuda.label;
  return (
    <button
      type="button"
      onClick={() => state.setOpen(true)}
      aria-label={label}
      title={label}
      className={cn(
        'fixed right-4 top-4 z-30 flex h-9 w-9 items-center justify-center',
        'rounded-full border border-line bg-surface text-ink shadow-sm',
        'transition-transform duration-160 ease-out hover:bg-surface-2 active:scale-[0.97]',
        'md:right-6 md:top-3',
      )}
      data-testid="ajuda-button"
    >
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-4 w-4"
      >
        <circle cx={12} cy={12} r={10} />
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
        <line x1={12} y1={17} x2={12.01} y2={17} />
      </svg>
    </button>
  );
}

function AjudaDrawer() {
  const state = useAjudaState();
  const { t } = useT();
  // ESC fecha o drawer.
  useEffect(() => {
    if (!state?.open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && state) state.setOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state, state?.open]);

  if (!state || !state.ctx) return null;
  const { ctx, open, setOpen } = state;
  if (!open) return null;

  const labelTela = t.ajuda.tela;
  const labelProx = t.ajuda.proximaAcao;
  const labelGlos = t.ajuda.glossario;
  const labelFechar = t.common.close;

  return (
    <>
      <div
        aria-hidden="true"
        onClick={() => setOpen(false)}
        className="fixed inset-0 z-40 bg-ink/40 backdrop-blur-[2px]"
      />
      <aside
        role="dialog"
        aria-labelledby="ajuda-title"
        className={cn(
          'fixed right-0 top-0 z-50 h-full w-[320px] max-w-full',
          'border-l border-line bg-surface text-ink shadow-xl',
          'flex flex-col',
        )}
        data-testid="ajuda-drawer"
      >
        <header className="flex items-center justify-between border-b border-line px-4 py-3">
          <h2 id="ajuda-title" className="text-base font-semibold">
            {t.ajuda.label}
          </h2>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label={labelFechar}
            className="rounded p-1 text-muted hover:bg-surface-2"
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-4 w-4"
            >
              <line x1={18} y1={6} x2={6} y2={18} />
              <line x1={6} y1={6} x2={18} y2={18} />
            </svg>
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-4 py-4 text-sm">
          <section className="mb-5">
            <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">
              {labelTela}
            </h3>
            <p className="font-medium">{ctx.title}</p>
            <p className="mt-1 text-muted">{ctx.description}</p>
          </section>
          {ctx.nextAction && (
            <section className="mb-5">
              <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">
                {labelProx}
              </h3>
              {ctx.nextAction.to ? (
                <a
                  href={ctx.nextAction.to}
                  className="text-brand-500 underline-offset-2 hover:underline"
                >
                  {ctx.nextAction.label}
                </a>
              ) : (
                <p>{ctx.nextAction.label}</p>
              )}
            </section>
          )}
          {ctx.glossary && ctx.glossary.length > 0 && (
            <section>
              <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">
                {labelGlos}
              </h3>
              <dl className="space-y-2">
                {ctx.glossary.map((g) => (
                  <div key={g.term}>
                    <dt className="font-medium">{g.term}</dt>
                    <dd className="text-muted">{g.meaning}</dd>
                  </div>
                ))}
              </dl>
            </section>
          )}
        </div>
      </aside>
    </>
  );
}

/**
 * Monta o par botão + drawer. AppShell deve renderizar uma única
 * instância (dentro do Provider). Quando não há contexto publicado,
 * ambos retornam null silenciosamente — zero ruído visual.
 */
export function AjudaContextualMount() {
  return (
    <>
      <AjudaButton />
      <AjudaDrawer />
    </>
  );
}

/**
 * Exposed também como default pra possibilitar `<AjudaContextual />`
 * em testes que não usam o provider (com fallback inerte).
 */
export default AjudaContextualMount;
